import type { ReactNode } from 'react';
import type { Warehouse } from '@features/warehouse/types';

import { useMemo, useContext, createContext } from 'react';

import { FatalError } from '@shared/components/fatal-error';

import { useUser } from '@features/user-access';

type WarehouseContextType = {
  warehouse: Warehouse;
};

// 1. Créer le Context avec une valeur par défaut de `null`
//    C'est normal à ce niveau, car en dehors du Provider, il n'y a pas de données.
const WarehouseContext = createContext<WarehouseContextType | null>(null);

// 2. Créer le Provider
export const WarehouseProvider = ({ children }: { children: ReactNode }) => {
  const {
    userAccess: { warehouse },
  } = useUser();

  const value = useMemo(
    () => ({
      // On doit s'assurer que companySettings est défini ici
      // Mais comme on le vérifie juste après, on peut utiliser un "non-null assertion" (!)
      // ou le laisser potentiellement undefined dans la memo, la vérification ci-dessous fera le travail.
      warehouse: warehouse!,
    }),
    [warehouse]
  );

  // En cas d'erreur critique qui empêche l'app de fonctionner
  if (!warehouse) {
    // Vous pouvez logguer l'erreur `error` ici
    return <FatalError />;
  }

  // 3. Si tout va bien, on fournit les données via le Provider
  return <WarehouseContext.Provider value={value}>{children}</WarehouseContext.Provider>;
};

type UseWarehouseType = {
  warehouse: Warehouse;
};

// 4. Créer le hook final `useWarehouse`
export const useWarehouse = (): UseWarehouseType => {
  const context = useContext(WarehouseContext);

  // Le hook lèvera une erreur s'il est utilisé hors du Provider,
  // ce qui garantit que la valeur n'est jamais `null` là où on l'utilise.
  if (context === null) {
    throw new Error("useWarehouse() doit être utilisé à l'intérieur d'un WarehouseProvider");
  }

  return {
    ...context,
  };
};
